import { useEffect } from 'react';
import { FormikProps, useFormik } from 'formik';
import * as Yup from 'yup';

import { IOrganization } from 'models';
import { useAddOrgThumbnailMutation, useUpdateOrgMutation } from 'store/apis/org.api';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';
import { useAppSelector } from 'store/store.hook';

const maxFileSize = 1000 * 1000 * 10;
const maxFileFriendly = '10MB';

interface UseCompanyFormManagerProps {
  next?: (data?: any) => void;
}

export const useCompanyFormManager = ({ next = () => {} }: UseCompanyFormManagerProps) => {
  const { org, orgSlug } = useAppSelector(selectCurrentUserInfo);

  const [
    updateOrg,
    { isLoading: isUpdatingOrg, isSuccess: isUpdatingOrgSuccess, isError: isUpdatingOrgError, ...rest },
  ] = useUpdateOrgMutation();

  const [
    addOrgThumbnail,
    {
      isLoading: isUploadingOrgThumbnail,
      isSuccess: isUploadingOrgThumbnailSuccess,
      isError: isUploadingOrgThumbnailError,
    },
  ] = useAddOrgThumbnailMutation();

  const initialValues = {
    avatar: null as File | null,
    name: org?.name || '',
    slug: org?.slug || '',
    primary_brand_color: org?.primary_brand_color || '#000000',
    secondary_brand_color: org?.secondary_brand_color || '#000000',
  };

  const validationSchema = Yup.object({
    avatar: Yup.mixed()
      .test('fileSize', `Please choose a file under ${maxFileFriendly}.`, (file) =>
        file ? file.size <= maxFileSize : true,
      )
      .test(
        'fileType',
        'Please choose a .png, .jpg, .jpeg, or .svg file.',
        (value) => !value || (value && ['image/png', 'image/jpg', 'image/jpeg', 'image/svg'].includes(value.type)),
      ),
    name: Yup.string().nullable().required('Company name is required.'),
    primary_brand_color: Yup.string().nullable(),
    secondary_brand_color: Yup.string().nullable(),
  });

  const formManager = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      const { avatar, ...rest } = values;

      if (avatar) {
        const bodyFormData = new FormData();
        bodyFormData.append('logo', avatar);

        addOrgThumbnail({ orgSlug, data: bodyFormData });
      } else {
        updateOrg({
          orgSlug,
          data: rest,
        });
      }
    },
  });

  useEffect(() => {
    if (!isUploadingOrgThumbnail && isUploadingOrgThumbnailSuccess && !isUploadingOrgThumbnailError) {
      const { avatar, ...rest } = formManager.values; // eslint-disable-line

      updateOrg({ orgSlug, data: rest });
    }
  }, [isUploadingOrgThumbnail, isUploadingOrgThumbnailSuccess, isUploadingOrgThumbnailError]);

  useEffect(() => {
    if (!isUpdatingOrg && isUpdatingOrgSuccess && !isUpdatingOrgError) {
      const { data } = rest;

      next(data);
    }
  }, [isUpdatingOrg, isUpdatingOrgSuccess, isUpdatingOrgError]);

  return [{ org, formManager, loading_submit: isUploadingOrgThumbnail || isUpdatingOrg }] as [
    { org: IOrganization; formManager: FormikProps<typeof initialValues>; loading_submit: boolean },
  ];
};
